import React, {useEffect, useRef, useState} from "react";
import {DeskingTable} from "./DeskingTable";
import './DeskingPage.scss'

import {DeskingPageLoading} from "./DeskingPageLoading";
import {useDeskingContext} from "../../contexts/DeskingContext";
import {DeskingFilterBar} from "./DeskingFilterBar";
import DeskingHelper, {defaultTermsForDeal} from "../../utils/DeskingHelper";
import {useThemeStore} from "../../stores/themeStore";
import {RebatesTable} from "../RebatesPage/RebatesTable";
import {useDebounce} from "@uidotdev/usehooks";
import {useUserContext} from "../../contexts/UserContext";
import {useModalsStore} from "../../stores/modalsStore";


// Saved default terms are kept separately per deal type — lease vs finance use
// different term sets, so each gets its own key in default_desking_settings.filters.
const termsFieldFor = (state) => (state?.is_purchase ? 'selected_terms_finance' : 'selected_terms_lease')

const SORT_MODES = ['Lender', 'BestPayment', 'BestLender']

export const DeskingPage = ( { id, onSave, setIsOpen, credit_score } ) => {
    const theme = useThemeStore((state) => state.theme)
    const { profileState, setProfileStateWithUpdate } = useUserContext()
    const closeModalById = useModalsStore((state) => state.closeModalById)
    const {
        deskingState,
        fetchDeskingResponse,
        updateDealResponse,
        setDeskingStateWithUpdate,
    } = useDeskingContext()

    // Re-price the matrix with the newly selected rebates (refetches desking).
    const fetchDeskingWithRebates = (rebates, option_code_ids) => {
        setDeskingStateWithUpdate({
            ...deskingState,
            rebates,
            option_code_ids,
        }, true)
    }

    // ── Filter bar state (view-only; computed over the matrix each render) ──
    // Saved defaults live on the profile under `default_desking_settings`:
    // a `filters` object plus a sibling `sort_by` string. Each filter seeds
    // from it when present, otherwise falls back to its prior default.
    const deskingSettings = profileState?.default_desking_settings || {}
    const savedDefaults = deskingSettings.filters || {}

    // Seed the Credit Score filter from the credit_score passed into the modal
    // (e.g. dealState.credit_score when opened from the DealPage). Credit score
    // is deal-specific, so it is NOT part of the saved defaults.
    const [creditScore, setCreditScore] = useState(credit_score ? String(credit_score) : '')
    // Debounce the value that drives quote filtering (400ms) so re-filtering the
    // whole grid doesn't run on every keystroke; the input itself stays live.
    const debouncedCreditScore = useDebounce(creditScore, 400)
    // Best by Lender is the default: only the best tier per lender, no tier
    // column. Toggling it off switches to "All Tiers".
    const [bestByLender, setBestByLender] = useState(savedDefaults.best_by_lender ?? true)
    const [captiveOnly, setCaptiveOnly] = useState(savedDefaults.captive_only ?? false)
    // Sort mode: `default_desking_settings.sort_by` is the source of truth.
    // 'Lender' (every offer), 'BestPayment' (by monthly payment) or 'BestLender'
    // (one offer per lender, as before captive offers).
    const savedSortBy = SORT_MODES.includes(deskingSettings.sort_by) ? deskingSettings.sort_by : 'Lender'
    const [sortBy, setSortBy] = useState(savedSortBy)
    // Allow quotes that exceed the lender's max advance (persisted; grid-level
    // filtering is a no-op until quotes carry a max-advance field).
    const [exceedMaxAdvance, setExceedMaxAdvance] = useState(savedDefaults.exceed_max_advance ?? false)
    const [termMode, setTermMode] = useState('specific')        // 'range' | 'specific'
    const [termRange, setTermRange] = useState({ min: '', max: '' })
    // Saved-default terms (per deal type) win; otherwise the deal-type term set.
    // Seeded eagerly when the deal is already loaded; the effect below covers the
    // load-after-mount case and runs only once so it doesn't clobber later edits.
    const [selectedTerms, setSelectedTerms] = useState(() =>
        savedDefaults[termsFieldFor(deskingState)] ?? defaultTermsForDeal(deskingState)
    )

    const didInitTerms = useRef(false)
    useEffect(function initDefaultTerms(){
        if (didInitTerms.current || !deskingState) return
        didInitTerms.current = true
        // Re-seed from the per-type saved default for the now-known deal type;
        // fall back to the deal-type term set when nothing is saved.
        const savedTerms = profileState?.default_desking_settings?.filters?.[termsFieldFor(deskingState)]
        setSelectedTerms(savedTerms ?? defaultTermsForDeal(deskingState))
        // Runs once after the deal loads (guarded by didInitTerms); the saved-
        // defaults read is intentionally not a dependency.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deskingState])

    useEffect(function onDeskingSave(){
        if (updateDealResponse?.data?.data && !updateDealResponse.loading && !updateDealResponse.error) {
            onSave && onSave()
        }
    }, [updateDealResponse])

    if (fetchDeskingResponse?.loading && !deskingState?.quotes) {
        return <DeskingPageLoading/>
    }

    if (!deskingState) return null;

    const carDetail = deskingState?.car_detail || {}
    const optionCodes = deskingState?.option_codes || []
    const isLoadingDesking = fetchDeskingResponse?.loading

    const headerTitle = {
        true: 'Lease',
        [deskingState.is_purchase]: 'Purchase',
        [deskingState.is_purchase && deskingState.is_one_pay]: 'Cash'
    }.true

    const helper = new DeskingHelper(deskingState.quotes, deskingState)
    const terms = helper.terms()
    const hasTiers = helper.tiers().length > 0
    const captiveLenderCount = helper.captiveLenders().size

    const filters = {
        creditScore: debouncedCreditScore, bestByLender, captiveOnly,
        sortByBestPayment: sortBy === 'BestPayment', bestOfferPerLender: sortBy === 'BestLender',
        termMode, termRange, selectedTerms, exceedMaxAdvance,
    }

    // ── Save-as-defaults (profile.default_desking_settings) ──
    // The button only offers itself when the current filters differ from what's
    // saved (or from the fallback defaults when nothing has been saved yet).
    const termsKey = (arr) => [...(arr || [])].map(Number).sort((a, b) => a - b).join(',')
    const baselineTerms = savedDefaults[termsFieldFor(deskingState)] ?? defaultTermsForDeal(deskingState)
    const filtersDirty =
        bestByLender !== (savedDefaults.best_by_lender ?? true) ||
        sortBy !== savedSortBy ||
        captiveOnly !== (savedDefaults.captive_only ?? false) ||
        exceedMaxAdvance !== (savedDefaults.exceed_max_advance ?? false) ||
        termsKey(selectedTerms) !== termsKey(baselineTerms)

    const saveFilterDefaults = () => {
        setProfileStateWithUpdate({
            default_desking_settings: {
                ...deskingSettings,
                filters: {
                    ...savedDefaults,   // preserve the other deal type's terms (and any other saved keys)
                    best_by_lender: bestByLender,
                    captive_only: captiveOnly,
                    exceed_max_advance: exceedMaxAdvance,
                    [termsFieldFor(deskingState)]: selectedTerms,
                },
                sort_by: sortBy,
            },
        })
    }

    return (
        <div id="DeskingPage" data-theme={theme}>
            <div className={`desking-panel${isLoadingDesking ? ' is-loading' : ''}${captiveLenderCount > 0 ? ' has-captive' : ''}`}>

                {/* Topbar */}
                <div className={'desking-topbar'}>
                    {/* Close — full-screen on mobile has no backdrop to tap, so
                        give an explicit X (hidden on desktop via CSS). */}
                    <button
                        type="button"
                        className={'desking-topbar__close'}
                        aria-label={'Close'}
                        onClick={() => closeModalById('DeskingModal')}
                    >
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="6" y1="6" x2="18" y2="18"/>
                            <line x1="18" y1="6" x2="6" y2="18"/>
                        </svg>
                    </button>
                    <span className={'desking-topbar__title'}>
                        Quotes for
                        <strong> {carDetail.year} {carDetail.make} {carDetail.model} </strong>
                        {carDetail.trim}
                    </span>
                    <span className={`desking-tag desking-tag--${headerTitle.toLowerCase()}`}>{headerTitle}</span>
                    {
                        optionCodes.length > 0 &&
                        <span className={'desking-topbar__options'}>
                            {optionCodes.map((o, i) => <span key={i}>{o.name}{i < optionCodes.length - 1 ? ', ' : ''}</span>)}
                        </span>
                    }
                </div>

                {/* Filter bar */}
                <DeskingFilterBar
                    hasTiers={hasTiers}
                    terms={terms}
                    captiveLenderCount={captiveLenderCount}
                    creditScore={creditScore}
                    setCreditScore={setCreditScore}
                    bestByLender={bestByLender}
                    setBestByLender={setBestByLender}
                    captiveOnly={captiveOnly}
                    setCaptiveOnly={setCaptiveOnly}
                    sortBy={sortBy}
                    setSortBy={setSortBy}
                    termMode={termMode}
                    setTermMode={setTermMode}
                    termRange={termRange}
                    setTermRange={setTermRange}
                    selectedTerms={selectedTerms}
                    setSelectedTerms={setSelectedTerms}
                    exceedMaxAdvance={exceedMaxAdvance}
                    setExceedMaxAdvance={setExceedMaxAdvance}
                    canSaveDefaults={filtersDirty}
                    onSaveDefaults={saveFilterDefaults}
                />

                {/* Matrix grid */}
                <DeskingTable
                    dealState={deskingState}
                    dealId={id}
                    onSave={onSave}
                    filters={filters}
                />

                {/* Rebates drawer — pinned to the bottom of the modal; its own
                    collapse opens the table upward over the grid. */}
                <div className={'desking-rebates-drawer'}>
                    <RebatesTable
                        {...deskingState}
                        applySelectedRebates={fetchDeskingWithRebates}
                    />
                </div>
            </div>
        </div>
    )
}
